import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import {
    Card,
    Button,
    Typography,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Menu,
    MenuHandler,
    MenuList,
    MenuItem,
    Tooltip,
} from "@material-tailwind/react";
import dayjs from "dayjs";
import { Form, Formik } from "formik";
import ContentWrapper from "components/ui/content-wrapper";
import useSWR from "swr";
import * as Yup from "yup";
import { fetcherFileUpload, fetcherFull, fetcherSimple } from "utils/api";
import { API_SERVER, STRIPE_PORTAL_LINK } from "utils/config";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { userSelector, fetchSubscription } from "store/user";
import { metaSelector, setPendingDowngradeDialogOpen } from "store/meta";
import { updateUserStatus } from "store/user";
import FormikInput from "components/form/formik-input";
import UploadPicture from "assets/img/uploadprofilepic.png";
import ManageUserDialog from "components/dialog/manage-user-dialog";
import UserRolesDialog from "components/dialog/user-roles-dialog";
import PendingDowngradeDialog from "components/dialog/pending-downgrade-dialog";
import InfoDialog from "components/dialog/info-dialog";
import MaterialIcon from "components/material-icon";
import { getValidUserLimit, resolvePlanLabel } from "utils/consts";
import { sanitiseText } from "utils/utils.jsx";
import {
    GobbyTable,
    GobbyTableHead,
    GobbyTableBody,
    GobbyTableRow,
    GobbyTableHeader,
    GobbyTableCell,
} from "components/ui/gobby-table";

export default function Organisation() {
    const dispatch = useDispatch();
    const userStore = useSelector(userSelector);
    const meta = useSelector(metaSelector);

    // Helper to check if user has an active paid subscription
    const hasActiveSubscription = userStore?.activeSubscription === true;
    const isManaged = userStore?.isManaged === true;

    const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
    const [showManageUserDialog, setShowManageUserDialog] = useState(false);
    const [showRemoveUserDialog, setShowRemoveUserDialog] = useState(false);
    const [showUserRolesDialog, setShowUserRolesDialog] = useState(false);
    const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
    const [showCreateRoleDialog, setShowCreateRoleDialog] = useState(false);
    const [showManageRoleDialog, setShowManageRoleDialog] = useState(false);
    const [showDeleteRoleDialog, setShowDeleteRoleDialog] = useState(false);
    const [editingUser, setEditingUser] = useState(undefined);
    const [editingRole, setEditingRole] = useState(undefined);
    const [showEditOrgNameDialog, setShowEditOrgNameDialog] = useState(false);
    const [editingOrgName, setEditingOrgName] = useState("");
    const [open, setOpen] = useState(0);
    const [organisationTab, setOrganisationTab] = useState("users");
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("name");

    const [profilePicDeletable, setProfilePicDeletable] = useState(true);
    const [inviteDisabled, setInviteDisabled] = useState(false);
    // timestamp value function with dayjs
    const [timestamp, setTimestamp] = useState(dayjs());
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        dispatch(fetchSubscription());
        if (searchParams.get("return_from_stripe") === "true") {
            toast.success("Subscription updated");
            setSearchParams((params) => {
                params.delete("return_from_stripe");
                return params;
            });
        }
    }, [dispatch, searchParams, setSearchParams]);

    // Refs
    const inputLogoFileRef = useRef(null);

    const {
        data: orgData,
        error: orgDataError,
        mutate: orgDataMutate,
        isValidating: orgDataValidating,
    } = useSWR([`/v2/organisation`, "GET"], ([path, method]) =>
        fetcherSimple(path, method)
    );

    const {
        data: usersList,
        error: usersListError,
        mutate: usersListMutate,
        isValidating: usersListIsValidating,
    } = useSWR([`/v2/organisation/users?status=active`, "GET"], ([path, method]) =>
        fetcherSimple(path, method)
    );

    const {
        data: rolesList,
        error: rolesListError,
        mutate: rolesListMutate,
        isValidating: rolesListIsValidating,
    } = useSWR([`/v2/organisation/roles`, "GET"], ([path, method]) =>
        fetcherSimple(path, method)
    );

    const imageUrl =
        API_SERVER +
        `/v2/user/${editingUser?.id}/picture`;

    const freeSeats = getValidUserLimit(orgData?.user_limit) - (usersList?.length || 0);
    const isInviteDisabled = organisationTab === "users" && freeSeats <= 0;

    const validationSchemaUser = Yup.object().shape({
        name: Yup.string().optional(),
        alt_email: Yup.string().email("Invalid email address").optional(),
        phone: Yup.string().optional(),
        address_1: Yup.string().optional(),
        address_2: Yup.string().optional(),
        postcode: Yup.string().optional(),
        country: Yup.string().optional(),
        job_role: Yup.string().optional(),
    });

    const validationSchemaInvite = Yup.object().shape({
        email: Yup.string().email("Invalid email address").required("Please enter a valid email address"),
    });

    const validationSchemaOrgName = Yup.object().shape({
        name: Yup.string()
            .required("Organisation name is required")
            .min(2, "Organisation name must be at least 2 characters")
            .max(100, "Organisation name must be less than 100 characters"),
    });

    const handleLogoUploadClick = () => {
        inputLogoFileRef.current?.click();
    };

    const profilePicUploadHandler = async (event) => {
        const csvSubmitToast = toast.loading("Saving Image...");
        if (!event.target.files) {
            toast.error("No file uploaded!");
            return;
        }
        const selectedFile = event.target.files[0];
        if (!selectedFile.type.includes("image/")) {
            toast.error(
                "Only Image Files are supported. Please ensure that the file you are loading is not excessively large and has the correct extension and name."
            );
            return;
        }
        if (!editingUser?.id) {
            toast.error("No user selected for profile picture upload");
            return;
        }
        let imageResponse = undefined;
        try {
            imageResponse = await fetcherFileUpload(
                `/v2/user/${editingUser.id}/picture`,
                "POST",
                "picture",
                selectedFile
            );
        } catch {
            const serverError =
                imageResponse ||
                "unknown issue in uploading image. Please try a different size or type.";
            toast.update(csvSubmitToast, {
                render: `Error uploading Image, ${serverError}.`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        }
        if (imageResponse) {
            toast.update(csvSubmitToast, {
                render: "Profile Picture Uploaded",
                type: "success",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            setTimestamp(dayjs());
            setProfilePicDeletable(true);
        } else {
            const serverError =
                imageResponse ||
                "unknown issue in uploading image. Please try a different size or type.";
            toast.update(csvSubmitToast, {
                render: `Error uploading Image, ${serverError}`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            console.log(imageResponse.errors);
        }
    };

    const handleDeleteProfilePic = async () => {
        if (!editingUser?.id) {
            toast.error("No user selected for profile picture upload");
            return;
        }
        const deleteResponse = await fetcherSimple(
            `/v2/user/${editingUser.id}/picture`,
            "DELETE"
        );
        if (deleteResponse && deleteResponse.deleted) {
            toast.success("Profile Picture Deleted!");
            setProfilePicDeletable(false);
            setTimestamp(dayjs());
        } else if (deleteResponse && deleteResponse.error) {
            toast.error(`Error deleting picture: ${deleteResponse.message}`);
        } else {
            toast.error("Error deleting picture!");
        }
    };

    const handleUserSubmit = async (values) => {
        setShowCreateUserDialog(false);
        setShowManageUserDialog(false);
        let user_id_editing = editingUser?.id;
        let sanitary_values = {};
        for (const property in values) {
            if (values[property]) {
                sanitary_values[property] = values[property];
            }
        }

        // Sanitize name fields before submission
        if (sanitary_values.name) {
            sanitary_values.name = sanitiseText(sanitary_values.name);
        }

        const userSubmitToast = toast.loading("Saving User...");
        let response = undefined;
        let serverError = undefined;
        const isInviteFlow = !user_id_editing && !sanitary_values.password && sanitary_values.email;

        if (isInviteFlow) {
            // Invite flow - only send email and url
            try {
                response = await fetcherFull(
                    `/v2/organisation/user`,
                    "POST",
                    {
                        email: sanitary_values.email,
                        url: `${window.location.origin}/invite/`
                    }
                );
            } catch (error) {
                serverError = error || response || "Unknown issue";
            }
            // The API returns a user object directly, not wrapped in a success property
            // Check for the presence of user data (id or email) to determine success
            if (response && (response?.id || response?.email)) {
                toast.update(userSubmitToast, {
                    render: "Invite sent successfully",
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
                usersListMutate();
            } else if (serverError) {
                toast.update(userSubmitToast, {
                    render: `Error sending invite, ${serverError || "unknown error"}`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
            } else {
                toast.update(userSubmitToast, {
                    render: `Error sending invite, unknown error`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
            }
        } else if (user_id_editing && typeof user_id_editing === "number") {
            try {
                response = await fetcherFull(
                    `/v2/user/${user_id_editing}`,
                    "PUT",
                    sanitary_values
                );
            } catch (error) {
                serverError = error?.message || "Unknown issue";
                console.error("API Error:", error);
            }

            // More robust success check - check for user ID or email as indicators of successful update
            if (response && (response?.id || response?.email)) {
                toast.update(userSubmitToast, {
                    render: "User Updated",
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
                usersListMutate();
            } else if (serverError) {
                toast.update(userSubmitToast, {
                    render: `Error updating User, ${
                        serverError || "unknown error"
                    }`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
            } else {
                console.log("DEBUG: User update response validation failed - Response:", response);
                toast.update(userSubmitToast, {
                    render: `Error updating User - Invalid response from server`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
            }
        } else if (!user_id_editing && sanitary_values) {
            try {
                response = await fetcherFull(
                    `/v2/organisation/user`,
                    "POST",
                    sanitary_values
                );
            } catch (error) {
                serverError = error || response || "Unknown issue";
            }
            if (response && response?.name) {
                toast.update(userSubmitToast, {
                    render: "User created",
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
                usersListMutate();
            } else if (serverError) {
                toast.update(userSubmitToast, {
                    render: `Error creating User, ${
                        serverError || "unknown error"
                    }`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
            } else {
                toast.update(userSubmitToast, {
                    render: `Error creating User, unknown error`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
            }
        } else {
            toast.update(userSubmitToast, {
                render: `Error updating User`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        }
        user_id_editing = undefined;
    };

    const handleInviteSubmit = async (values) => {
        if (inviteDisabled) {
            toast.error('Please wait before sending another invite.');
            return;
        }
        setInviteDisabled(true);
        setShowCreateUserDialog(false);
        const inviteToast = toast.loading("Sending invite...");
        let response = undefined;
        let serverError = undefined;
        try {
            response = await fetcherFull(
                `/v2/organisation/user`,
                "POST",
                {
                    email: values.email,
                    url: `${window.location.origin}/invite/`
                }
            );
        } catch (error) {
            serverError = error || response || "Unknown issue";
        }
        // The API returns a user object directly, not wrapped in a success property
        // Check for the presence of user data (id or email) to determine success
        if (response && (response?.id || response?.email)) {
            toast.update(inviteToast, {
                render: "Invite sent successfully",
                type: "success",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            usersListMutate();
        } else if (serverError) {
            toast.update(inviteToast, {
                render: `Error sending invite, ${serverError || "unknown error"}`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        } else {
            toast.update(inviteToast, {
                render: `Error sending invite, unknown error`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        }
        setTimeout(() => setInviteDisabled(false), 5000);
    };

    const handleRoleSubmit = async (values) => {
        setShowCreateUserDialog(false);
        setShowManageUserDialog(false);
        console.log(values);
        const user_id_editing = editingUser?.id;
        let sanitary_values = {};
        for (const property in values) {
            if (values[property]) {
                sanitary_values[property] = values[property];
            }
        }
        console.log(sanitary_values);
        const roleSubmitToast = toast.loading("Saving Role...");
        let response = undefined;
        let serverError = undefined;
        if (user_id_editing && typeof user_id_editing === "number") {
            if (!editingRole?.id) {
                toast.error("Role data not available for update");
                return;
            }
            try {
                response = await fetcherFull(
                    `/v2/organisation/roles/${editingRole.id}`,
                    "PUT",
                    sanitary_values
                );
            } catch {
                serverError = response || "Unknown issue";
            }
            if (response && response?.name) {
                toast.update(roleSubmitToast, {
                    render: "Role Updated",
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
                rolesListMutate();
            } else if (serverError) {
                toast.update(roleSubmitToast, {
                    render: `Error updating Role, ${
                        serverError || "unknown error"
                    }`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
            } else {
                toast.update(roleSubmitToast, {
                    render: `Error updating Role, 'unknown error'`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
            }
        } else if (!user_id_editing && sanitary_values) {
            try {
                response = await fetcherFull(
                    `/v2/organisation/roles`,
                    "POST",
                    sanitary_values
                );
            } catch {
                serverError = response || "Unknown issue";
            }
            if (response && response?.name) {
                toast.update(roleSubmitToast, {
                    render: "Role created",
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
                usersListMutate();
            } else if (serverError) {
                toast.update(roleSubmitToast, {
                    render: `Error creating Role, ${
                        serverError || "unknown error"
                    }`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
            } else {
                toast.update(roleSubmitToast, {
                    render: `Error creating Role, unknown error`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                    pauseOnFocusLoss: false,
                    pauseOnHover: false,
                    closeOnClick: true,
                    closeButton: true,
                });
            }
        } else {
            toast.update(roleSubmitToast, {
                render: `Error creating Role, ${"unknown error"}`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        }
    };

    const handleRemoveUserDialogClose = () => {
        setEditingUser(undefined);
        setShowRemoveUserDialog(false);
    };

    const handleDeleteRoleDialogClose = () => {
        setEditingRole(undefined);
        setShowDeleteRoleDialog(false);
    };

    const handleRemoveUser = async () => {
        if (editingUser) {
            const isDowngradePending =
                userStore.subscriptionData?.pending_seat_change < 0;

            if (isDowngradePending) {
                // A downgrade is pending, so we should ask the user if they want to
                // deactivate immediately or schedule it.
                setShowRemoveUserDialog(false);
                setShowDowngradeConfirm(true);
                return;
            }

            // Standard deactivation
            setShowRemoveUserDialog(false);
            const userId = editingUser.id;

            if (userId && typeof userId === "number") {
                const statusUpdate = {
                    userId,
                    active: false,
                    inactive_reason: "manual",
                };

                const resultAction = await dispatch(
                    updateUserStatus(statusUpdate)
                );
                if (updateUserStatus.fulfilled.match(resultAction)) {
                    toast.success(
                        "The user has been removed!"
                    );
                    usersListMutate();
                } else {
                    const errorMsg = resultAction.error?.message || "";
                    if (errorMsg.includes("minimum seat") || errorMsg.includes("seat count") || errorMsg.includes("at least one")) {
                        toast.error(
                            "Cannot remove user: Seat limit reached. Please contact Gobby support for assistance."
                        );
                    } else {
                        toast.error(
                            "There has been an error removing this user. Please try again, if the problem persists then contact Gobby support."
                        );
                    }
                }
            } else {
                toast.error(`We could find no user by that ID!`);
            }
            setEditingUser(undefined);
        } else {
            toast.error(`No user selected!`);
        }
    };

    const handleDeleteRole = async () => {
        if (!editingRole?.id) {
            toast.error("No role selected for deletion");
            setShowDeleteRoleDialog(false);
            return;
        }
        const role_id_deleting = editingRole.id;
        setEditingRole(undefined);
        if (role_id_deleting && typeof role_id_deleting === "number") {
            const response = await fetcherSimple(
                `/v2/organisation/roles/${role_id_deleting}`,
                "DELETE"
            );
            if (response.success) {
                if (rolesList) {
                    rolesListMutate({
                        ...rolesList,
                        data: rolesList.filter((u) => u.id !== role_id_deleting),
                    });
                }
                toast.success("The survey has been permanently removed!");
            } else {
                toast.error(
                    "There has been an error deleting this survey. Please try again, if the problem persists then contact Gobby support."
                );
            }
        } else {
            toast.error("We could find no survey by that name to remove!");
        }
    };

    const handleOrgNameSubmit = async (values) => {
        setShowEditOrgNameDialog(false);
        const orgNameToast = toast.loading("Saving Organisation Name...");
        const sanitisedName = sanitiseText(values.name);
        let response = undefined;
        try {
            response = await fetcherFull(
                `/v2/organisation`,
                "PUT",
                { name: sanitisedName }
            );
        } catch (error) {
            toast.update(orgNameToast, {
                render: `Error updating Organisation Name`,
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            return;
        }
        if (response?.name) {
            toast.update(orgNameToast, {
                render: "Organisation Name Updated",
                type: "success",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
            orgDataMutate();
        } else {
            toast.update(orgNameToast, {
                render: "Error updating Organisation Name",
                type: "error",
                isLoading: false,
                autoClose: 5000,
                pauseOnFocusLoss: false,
                pauseOnHover: false,
                closeOnClick: true,
                closeButton: true,
            });
        }
    };
    const handleSortByName = () => {
        setOrder(order === "asc" ? "desc" : "asc");
    };
    const sortedUsers =
        usersList &&
        [...usersList].sort((a, b) => {
            // Account holder (main admin) always comes first
            if (a.is_main_admin && !b.is_main_admin) return -1;
            if (!a.is_main_admin && b.is_main_admin) return 1;

            // Then sort by name for the rest
            const nameA = a.name?.toLowerCase() || "";
            const nameB = b.name?.toLowerCase() || "";

            if (nameA < nameB) return order === "asc" ? -1 : 1;
            if (nameA > nameB) return order === "asc" ? 1 : -1;
            return 0;
        });

    // Filter out archived users - they should be completely hidden from the interface
    const activeUsers = sortedUsers?.filter(u => u.active !== false) || [];

    return (
        <>
            <ContentWrapper>
                <div className="lg:p-6 p-4">
                    {userStore.subscriptionData?.pending_seat_change !== null &&
                     userStore.subscriptionData?.pending_seat_change < userStore.subscriptionData?.seats && (
                        <div className="bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4 mb-4 flex justify-between items-center" role="alert">
                            <p className="font-bold">
                                Your subscription has a pending seat reduction. Please select users for deactivation.
                            </p>
                            <Button
                                className="btn btn--primary ml-4"
                                onClick={() => dispatch(setPendingDowngradeDialogOpen(true))}
                            >
                                Select Team Members
                            </Button>
                        </div>
                    )}
                    <Card className="card flex justify-start p-4 min-h-[50vh]" shadow={true}>
                        <div className="md:flex justify-between items-center">
                            <div>
                                <div
                                    className={`flex items-center gap-2 ${
                                        userStore?.isAccountHolder
                                            ? "cursor-pointer hover:opacity-80 transition"
                                            : ""
                                    }`}
                                    onClick={() => {
                                        if (userStore?.isAccountHolder) {
                                            setEditingOrgName(orgData?.name || "");
                                            setShowEditOrgNameDialog(true);
                                        }
                                    }}
                                >
                                    <Typography
                                        as="h1"
                                        className={`text-2xl md:text-4xl font-display text-black ${
                                            userStore?.isAccountHolder
                                                ? "border-b-2 border-dotted border-gray-400"
                                                : ""
                                        }`}
                                    >
                                        {orgData?.name || "Organisation"}
                                    </Typography>
                                    {!!userStore?.isAccountHolder && (
                                        <MaterialIcon name="edit" size="sm" color="gray" />
                                    )}
                                </div>
                                <Typography className="text-lg font-display text-black">
                                    <span>
                                        {resolvePlanLabel(isManaged, userStore?.subscriptionDetails, orgData?.subscription_name)} -&nbsp;
                                    </span>
                                    <span className={usersList?.length > getValidUserLimit(orgData?.user_limit) ? "text-red-500" : "text-green-500"}>
                                        {`${usersList?.length || 0} of ${getValidUserLimit(orgData?.user_limit)} seats filled`}
                                    </span>
                                </Typography>
                            </div>
                            <div className="flex items-center gap-4">
                                {userStore?.isAccountHolder && (hasActiveSubscription || isManaged) && (
                                    organisationTab === "users" && isInviteDisabled ? (
                                        <Tooltip content={isManaged ? "No seats available. Contact support to add more seats." : "No seats available. Upgrade your plan to invite more team members."}>
                                            <div>
                                                <Button
                                                    className="btn btn--primary"
                                                    disabled
                                                >
                                                    Invite
                                                </Button>
                                            </div>
                                        </Tooltip>
                                    ) : (
                                        <Button
                                            className="btn btn--primary"
                                            onClick={() =>
                                                organisationTab === "roles"
                                                    ? setShowCreateRoleDialog(true)
                                                    : setShowCreateUserDialog(true)
                                            }
                                        >
                                            {organisationTab === "roles" ? "Create New Role" : "Invite"}
                                        </Button>
                                    )
                                )}
                                {!!userStore?.userData?.is_main_admin && hasActiveSubscription && !isManaged && (
                                    <div>
                                        <Button
                                            className="btn btn--primary"
                                            onClick={() =>
                                                window.open(
                                                    STRIPE_PORTAL_LINK + (userStore?.userData?.email ?
                                                        `?prefilled_email=${userStore?.userData?.email}` : ''),
                                                    "_blank"
                                                )
                                            }
                                        >
                                            Manage Seats
                                        </Button>
                                    </div>
                                )}

                                <div className="w-[2px] h-5 bg-black-100"></div>
                                <InfoDialog title="Organisation help">
                                    <Typography
                                        as="span"
                                        className="font-body text-primary"
                                    >
                                        Add Info
                                    </Typography>
                                </InfoDialog>
                            </div>
                        </div>
                        {/* <div className="flex flex-row">
                            <div className="py-2 flex-auto justify-start">
                                <Typography
                                    variant="h3"
                                    className="font-display p-2 rounded-lg mt-2 mb-4 pl-2"
                                >
                                    {organisationTab === "roles" ? "Role" : "User"}{" "}
                                    Admin
                                </Typography>
                            </div>
                            <div className="py-2 flex-none justify-end">
                                <Button onClick={() => setOrganisationTab('users')}
                                    className={organisationTab === 'users' ? 'btn btn--primary' : 'btn btn--secondary'}
                                    variant="filled">
                                    Users
                                </Button>
                                <Button onClick={() => setOrganisationTab('roles')}
                                    className={organisationTab === 'roles' ? 'btn btn--primary' : 'btn btn--secondary'}
                                    variant="filled">
                                    Roles
                                </Button>
                            </div>
                        </div> */}

                        <div className="bg-white rounded-2xl px-2 py-4 overflow-x-auto">
                            {!!(organisationTab === "roles") && (
                                <GobbyTable>
                                    <GobbyTableHead>
                                        <GobbyTableRow>
                                            <GobbyTableHeader>Name</GobbyTableHeader>
                                            <GobbyTableHeader>Permissions</GobbyTableHeader>
                                            <GobbyTableHeader>&nbsp;</GobbyTableHeader>
                                            <GobbyTableHeader>&nbsp;</GobbyTableHeader>
                                        </GobbyTableRow>
                                    </GobbyTableHead>
                                    <GobbyTableBody>
                                        {rolesList?.map((role, index) => (
                                            <GobbyTableRow key={role.id || `role-${index}`}>
                                                <GobbyTableCell>{role.name}</GobbyTableCell>
                                                <GobbyTableCell>PERMISSIONS HERE</GobbyTableCell>
                                                <GobbyTableCell>
                                                    <Button
                                                        type="button"
                                                        variant="filled"
                                                        className="btn btn--primary"
                                                        onClick={() => {
                                                            setEditingRole(
                                                                role
                                                            );
                                                            setShowManageRoleDialog(
                                                                true
                                                            );
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                </GobbyTableCell>
                                                <GobbyTableCell>
                                                    <Button
                                                        type="button"
                                                        variant="filled"
                                                        className="btn btn--danger"
                                                        onClick={() => {
                                                            setEditingRole(
                                                                role
                                                            );
                                                            setShowDeleteRoleDialog(
                                                                true
                                                            );
                                                        }}
                                                    >
                                                        Remove
                                                    </Button>
                                                </GobbyTableCell>
                                            </GobbyTableRow>
                                        ))}
                                    </GobbyTableBody>
                                </GobbyTable>
                            )}
                            {!!(organisationTab === "users") && (
                                <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                                    <GobbyTable>
                                        <GobbyTableHead>
                                            <GobbyTableRow>
                                                <GobbyTableHeader>
                                                    <div className="flex items-center gap-2">
                                                        <span>Name</span>
                                                        <span
                                                            className="cursor-pointer flex items-center hover:text-blue-600 transition"
                                                            onClick={
                                                                handleSortByName
                                                            }
                                                        >
                                                            {orderBy === "name" ? (
                                                                order === "asc" ? (
                                                                    <MaterialIcon
                                                                        name="arrow_upward"
                                                                        size="sm"
                                                                    />
                                                                ) : (
                                                                    <MaterialIcon
                                                                        name="arrow_downward"
                                                                        size="sm"
                                                                    />
                                                                )
                                                            ) : (
                                                                <MaterialIcon
                                                                    name="unfolded"
                                                                    size="sm"
                                                                />
                                                            )}
                                                        </span>
                                                    </div>
                                                </GobbyTableHeader>
                                                <GobbyTableHeader>Email</GobbyTableHeader>
                                                <GobbyTableHeader>Roles</GobbyTableHeader>
                                                <GobbyTableHeader>Status</GobbyTableHeader>
                                            </GobbyTableRow>
                                        </GobbyTableHead>
                                        <GobbyTableBody>
                                            {!!usersList && (
                                                <>
                                                    {(() => {
                                                        const activeUsersCount = activeUsers.length || 0;
                                                        const userLimit = getValidUserLimit(orgData?.user_limit);  // Defensive validation
                                                        const availableSeats = Math.max(0, userLimit - activeUsersCount);
                                                        const availableRows = availableSeats > 0 ? Array.from({ length: availableSeats }, (_, index) => ({
                                                            name: "Seat Available",
                                                            email: "",
                                                            roles: {},
                                                            active: null,
                                                        })) : [];
                                                        const tableData = [...activeUsers, ...availableRows];
                                                        return tableData.map((user, index) => (
                                                            <GobbyTableRow key={user.id || `available-${index}`}>
                                                                {!!user?.id && (
                                                                    <>
                                                                        <GobbyTableCell>{user.name}</GobbyTableCell>
                                                                        <GobbyTableCell>{user.email}</GobbyTableCell>
                                                                        <GobbyTableCell>{user.roles ? Object.values(user.roles).join(", ") : "N/A"}</GobbyTableCell>
                                                                        <GobbyTableCell>
                                                                            <div className="flex items-center justify-between w-full">
                                                                                <div className="flex items-center gap-2">
                                                                                    <span
                                                                                        className={`h-3 w-3 rounded-full ${
                                                                                            user?.invited && !user?.last_authed
                                                                                                ? "bg-yellow-500"
                                                                                                : user.active === true
                                                                                                    ? "bg-green-500"
                                                                                                    : "bg-gray-500"
                                                                                        }`}
                                                                                    ></span>
                                                                                    <span>
                                                                                        {user?.invited && !user?.last_authed
                                                                                            ? "Invited"
                                                                                            : user.active === true
                                                                                                ? "Active"
                                                                                                : "Available"}
                                                                                    </span>
                                                                                </div>
                                                                                {/* Account Holder - show full menu on all rows */}
                                                                                {user.id && userStore?.isAccountHolder && (
                                                                                    <Menu placement="left">
                                                                                        <MenuHandler>
                                                                                            <span className="cursor-pointer hover:text-blue-600 transition pr-5">
                                                                                                <MaterialIcon name="more_vert" size="xl" />
                                                                                            </span>
                                                                                        </MenuHandler>
                                                                                        <MenuList className="border-gray-700">
                                                                                            <MenuItem onClick={() => {
                                                                                                setEditingUser(user);
                                                                                                setShowManageUserDialog(true);
                                                                                            }}>Edit profile</MenuItem>
                                                                                            {(hasActiveSubscription || isManaged) && (
                                                                                                <MenuItem onClick={() => {
                                                                                                    setEditingUser(user);
                                                                                                    setShowUserRolesDialog(true);
                                                                                                }}>Manage Roles</MenuItem>
                                                                                            )}
                                                                                            {/* Only show Remove for OTHER members, not self and not main admin */}
                                                                                            {!user.is_main_admin && user.id !== userStore?.userData?.id && (
                                                                                                <MenuItem onClick={() => {
                                                                                                    setEditingUser(user);
                                                                                                    setShowRemoveUserDialog(true);
                                                                                                }}>Remove Team Member</MenuItem>
                                                                                            )}
                                                                                        </MenuList>
                                                                                    </Menu>
                                                                                )}
                                                                                {/* Team Member - show limited menu only on own row */}
                                                                                {user.id && !userStore?.isAccountHolder && user.id === userStore?.userData?.id && (
                                                                                    <Menu placement="left">
                                                                                        <MenuHandler>
                                                                                            <span className="cursor-pointer hover:text-blue-600 transition pr-5">
                                                                                                <MaterialIcon name="more_vert" size="xl" />
                                                                                            </span>
                                                                                        </MenuHandler>
                                                                                        <MenuList className="border-gray-700">
                                                                                            <MenuItem onClick={() => {
                                                                                                setEditingUser(user);
                                                                                                setShowManageUserDialog(true);
                                                                                            }}>Edit profile</MenuItem>
                                                                                        </MenuList>
                                                                                    </Menu>
                                                                                )}
                                                                            </div>
                                                                        </GobbyTableCell>
                                                                    </>
                                                                )}
                                                                {!user?.id && (
                                                                    <GobbyTableCell colSpan="4">{user.name}</GobbyTableCell>
                                                                )}
                                                            </GobbyTableRow>
                                                        ));
                                                    })()}
                                                </>
                                            )}
                                        </GobbyTableBody>
                                    </GobbyTable>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            </ContentWrapper>
            <Dialog
                open={showCreateUserDialog}
                handler={() =>
                    setShowCreateUserDialog(
                        (showCreateUserDialog) => !showCreateUserDialog
                    )
                }
                className="dialog"
            >
                <DialogHeader className="dialog__header">
                    Invite A Team Member
                </DialogHeader>
                <Formik
                    initialValues={{
                        email: "",
                    }}
                    onSubmit={handleInviteSubmit}
                    validationSchema={validationSchemaInvite}
                >
                    {({ isValid, dirty }) => (
                        <Form className="w-full">
                            <DialogBody className="dialog__body pt-2">
                                <div className="dialog__section flex flex-col gap-4 p-4">
                                    <FormikInput
                                        className="font-body input-black"
                                        labelProps="!text-black"
                                        name="email"
                                        type="email"
                                        label="Team Member Email"
                                        required
                                    />
                                </div>
                                <Typography variant="small" className="text-center text-black px-4">
                                    Please allow a minimum of 5 minutes for the invitee to receive their email.
                                </Typography>
                            </DialogBody>
                            <DialogFooter className="dialog__footer">
                                <Button
                                    type="button"
                                    className="btn btn--secondary"
                                    variant="filled"
                                    size="lg"
                                    onClick={() =>
                                        setShowCreateUserDialog(false)
                                    }
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={!(isValid && dirty) || inviteDisabled}
                                    className="btn btn--primary"
                                    variant="filled"
                                    size="lg"
                                >
                                    Invite
                                </Button>
                            </DialogFooter>
                        </Form>
                    )}
                </Formik>
            </Dialog>
            <Dialog
                open={showRemoveUserDialog}
                handler={() => handleRemoveUserDialogClose()}
                className="dialog"
            >
                <DialogHeader className="dialog__header">
                    Remove Team Member
                </DialogHeader>
                <DialogBody className="dialog__body">
                    This action cannot be undone and will make the user unable to log in as they will be considered
                    removed from the organisation. No Survey data will be deleted. Are you sure you want to proceed?
                </DialogBody>
                <DialogFooter className="dialog__footer">
                    <Button
                        type="button"
                        size="lg"
                        className="btn btn--secondary"
                        variant="filled"
                        onClick={() => handleRemoveUserDialogClose()}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        size="lg"
                        className="btn btn--primary"
                        variant="filled"
                        onClick={() => handleRemoveUser()}
                    >
                        Remove
                    </Button>
                </DialogFooter>
            </Dialog>
            <ManageUserDialog
                handleUserSubmit={handleUserSubmit}
                validationSchemaUser={validationSchemaUser}
                showManageUserDialog={showManageUserDialog}
                setShowManageUserDialog={setShowManageUserDialog}
                handleLogoUploadClick={handleLogoUploadClick}
                profilePicDeletable={profilePicDeletable}
                handleDeleteProfilePic={handleDeleteProfilePic}
                profilePicUploadHandler={profilePicUploadHandler}
                inputLogoFileRef={inputLogoFileRef}
                timestamp={timestamp}
                imageUrl={imageUrl}
                UploadPicture={UploadPicture}
                setProfilePicDeletable={setProfilePicDeletable}
                userData={editingUser}
            />
            <UserRolesDialog
                handleUserSubmit={handleUserSubmit}
                userData={editingUser}
                rolesList={rolesList}
                showUserRoleDialog={showUserRolesDialog}
                setShowUserRoleDialog={setShowUserRolesDialog}
            />
            <Dialog
                open={showEditOrgNameDialog}
                handler={() => setShowEditOrgNameDialog(false)}
                className="dialog"
            >
                <DialogHeader className="dialog__header">
                    Edit Organisation Name
                </DialogHeader>
                <Formik
                    initialValues={{ name: editingOrgName }}
                    onSubmit={handleOrgNameSubmit}
                    validationSchema={validationSchemaOrgName}
                >
                    {({ isValid, dirty }) => (
                        <Form className="w-full">
                            <DialogBody className="dialog__body pt-2">
                                <div className="dialog__section flex flex-col gap-4 p-4">
                                    <FormikInput
                                        className="font-body input-black"
                                        labelProps="!text-black"
                                        name="name"
                                        type="text"
                                        label="Organisation Name"
                                        required
                                    />
                                </div>
                            </DialogBody>
                            <DialogFooter className="dialog__footer">
                                <Button
                                    type="button"
                                    className="btn btn--secondary"
                                    variant="filled"
                                    size="lg"
                                    onClick={() => setShowEditOrgNameDialog(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={!(isValid && dirty)}
                                    className="btn btn--primary"
                                    variant="filled"
                                    size="lg"
                                >
                                    Save
                                </Button>
                            </DialogFooter>
                        </Form>
                    )}
                </Formik>
            </Dialog>
            {userStore.subscriptionData && (
                <PendingDowngradeDialog
                    open={meta.pendingDowngradeDialogOpen}
                    onClose={() => dispatch(setPendingDowngradeDialogOpen(false))}
                    users={usersList?.filter((u) => u.active === true && !u.is_main_admin) || []}
                    subscription={userStore.subscriptionData}
                    userLimit={getValidUserLimit(orgData?.user_limit)}  // Add this prop with defensive validation
                    onSuccess={() => {
                        usersListMutate();
                    }}
                />
            )}
            <Dialog
                open={showDowngradeConfirm}
                handler={() => setShowDowngradeConfirm(false)}
                className="dialog"
            >
                <DialogHeader className="dialog__header">Confirm Deactivation</DialogHeader>
                <DialogBody className="dialog__body">
                   A subscription downgrade is pending. Do you want to deactivate this user immediately or schedule the deactivation for the end of the billing cycle?
                </DialogBody>
                <DialogFooter className="dialog__footer">
                    <Button
                        type="button"
                        size="lg"
                        className="btn btn--secondary"
                        variant="filled"
                        onClick={() => {
                            // Mark user for deactivation at the end of the billing cycle
                            dispatch(updateUserStatus({ userId: editingUser.id, active: true, inactive_reason: "pending" }));
                            setShowDowngradeConfirm(false);
                            setEditingUser(undefined);
                        }}
                    >
                       Schedule for Deactivation
                    </Button>
                    <Button
                        type="button"
                        size="lg"
                        className="btn btn--primary"
                        variant="filled"
                        onClick={() => {
                            // Deactivate the user immediately
                            dispatch(updateUserStatus({ userId: editingUser.id, active: false, inactive_reason: "manual" }));
                            setShowDowngradeConfirm(false);
                            setEditingUser(undefined);
                        }}
                    >
                       Deactivate Immediately
                    </Button>
                </DialogFooter>
            </Dialog>
        </>
    );
}
